import React, { useEffect, useState } from "react";
import axios from "axios";
import Sidebar from "../components/Sidebar";
import { toast } from "react-hot-toast";
import { format } from "date-fns";

const API_BASE = process.env.REACT_APP_API_BASE;

const DisputeJobs = () => {
  const [disputes, setDisputes] = useState([]);
  const [selectedJob, setSelectedJob] = useState(null);
  const [resolutionType, setResolutionType] = useState("");
  const [refundAmount, setRefundAmount] = useState(0);
  const [workerAmount, setWorkerAmount] = useState(0);
  const [adminNotes, setAdminNotes] = useState("");

  /* ============================================================
     📡 FETCH DISPUTES
  ============================================================ */
  const fetchDisputes = async () => {
    try {
      const res = await axios.get(`${API_BASE}/admin/all-disputes`);
      setDisputes(res.data || []);
    } catch (err) {
      console.error(err);
      toast.error("Failed to load disputes");
    }
  };

  useEffect(() => {
    fetchDisputes();
  }, []);

  /* ============================================================
     🧮 AUTO BALANCE HELPERS
  ============================================================ */
  const paid = selectedJob?.customerPaidAmount || 0;

  const handleRefundChange = (value) => {
    const refund = Math.max(Number(value) || 0, 0);
    setRefundAmount(refund);
    setWorkerAmount(Math.max(paid - refund, 0));
  };

  const handleWorkerChange = (value) => {
    const payout = Math.max(Number(value) || 0, 0);
    setWorkerAmount(payout);
    setRefundAmount(Math.max(paid - payout, 0));
  };

  const remaining = paid - refundAmount - workerAmount;

  /* ============================================================
     🧾 SUBMIT RESOLUTION
  ============================================================ */
  const handleResolve = async () => {
    if (!resolutionType) {
      return toast.error("Please select a resolution type");
    }

    if (resolutionType === "partial_refund" && remaining < 0) {
      return toast.error("Refund + payout cannot exceed paid amount");
    }

    try {
      const res = await axios.post(
        `${API_BASE}/admin/resolve-dispute/${selectedJob._id}`,
        {
          resolution: resolutionType,
          refundAmount,
          workerAmount,
          adminNotes,
        }
      );

      toast.success(res.data.message || "Dispute resolved");
      setSelectedJob(null);
      setResolutionType("");
      setRefundAmount(0);
      setWorkerAmount(0);
      setAdminNotes("");
      fetchDisputes();
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data?.error || "Resolution failed");
    }
  };

  return (
    <div className="flex min-h-screen bg-gray-50">
      <Sidebar />

      <main className="flex-1 p-6 md:ml-64">
        <h1 className="text-3xl font-bold mb-8">⚖️ Disputed Jobs</h1>

        {disputes.length === 0 ? (
          <p>No active disputes.</p>
        ) : (
          <div className="space-y-6">
            {disputes.map((job) => (
              <div
                key={job._id}
                className="bg-white p-6 rounded-xl shadow border"
              >
                <div className="flex justify-between">
                  <h2 className="text-xl font-semibold">{job.jobTitle}</h2>
                  <span className="bg-red-100 text-red-700 px-3 py-1 rounded-full text-sm">
                    Dispute
                  </span>
                </div>

                <p className="mt-2 text-gray-600">{job.description}</p>

                {/* INFO GRID */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4 text-sm">
                  <div>
                    <h3 className="font-semibold">Customer</h3>
                    <p>{job.customerId?.name}</p>
                    <p>{job.customerId?.email}</p>
                  </div>

                  <div>
                    <h3 className="font-semibold">Worker</h3>
                    <p>{job.assignedTo?.name}</p>
                    <p>{job.assignedTo?.email}</p>
                  </div>

                  <div>
                    <h3 className="font-semibold">Financials</h3>
                    <p>Customer Paid: <strong>${job.customerPaidAmount}</strong></p>
                    <p>Assigned Price: ${job.assignedPrice || 0}</p>
                    <p>Payment Status: {job.paymentStatus}</p>
                  </div>

                  <div>
                    <h3 className="font-semibold">Timeline</h3>
                    <p>
                      Disputed At:{" "}
                      {job.completion?.disputeAt
                        ? format(new Date(job.completion.disputeAt), "PPpp")
                        : "N/A"}
                    </p>
                  </div>
                </div>

                {/* DISPUTE REASON */}
                <div className="mt-4 bg-red-50 border border-red-200 p-4 rounded">
                  <strong>Dispute Reason:</strong>
                  <p>{job.disputeReason}</p>
                </div>

                <button
                  onClick={() => {
                    setSelectedJob(job);
                    setRefundAmount(0);
                    setWorkerAmount(0);
                  }}
                  className="mt-4 bg-yellow-600 text-white px-4 py-2 rounded"
                >
                  Resolve Dispute
                </button>
              </div>
            ))}
          </div>
        )}

        {/* ============================================================
            ⚙️ RESOLUTION MODAL
        ============================================================ */}
        {selectedJob && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white p-6 rounded-lg w-full max-w-lg">
              <h2 className="text-xl font-bold mb-4">
                Resolve: {selectedJob.jobTitle}
              </h2>

              <p className="mb-2 text-sm">
                Customer Paid: <strong>${paid}</strong>
              </p>

              <select
                value={resolutionType}
                onChange={(e) => setResolutionType(e.target.value)}
                className="w-full border rounded px-3 py-2 mb-3"
              >
                <option value="">Select Resolution</option>
                <option value="refund_customer">Refund Customer</option>
                <option value="release_worker">Release Payment to Worker</option>
                <option value="partial_refund">Partial Split</option>
              </select>

              {resolutionType === "partial_refund" && (
                <div className="space-y-3 mb-4">
                  <input
                    type="number"
                    value={refundAmount}
                    onChange={(e) => handleRefundChange(e.target.value)}
                    className="w-full border rounded px-3 py-2"
                    placeholder="Refund to customer"
                  />
                  <input
                    type="number"
                    value={workerAmount}
                    onChange={(e) => handleWorkerChange(e.target.value)}
                    className="w-full border rounded px-3 py-2"
                    placeholder="Pay to worker"
                  />

                  <p
                    className={`text-xs ${
                      remaining < 0 ? "text-red-600" : "text-gray-600"
                    }`}
                  >
                    Remaining allocatable: ${remaining.toFixed(2)}
                  </p>
                </div>
              )}

              <textarea
                value={adminNotes}
                onChange={(e) => setAdminNotes(e.target.value)}
                className="w-full border rounded px-3 py-2 mb-4"
                rows={4}
                placeholder="Admin notes"
              />

              <div className="flex justify-end gap-3">
                <button
                  onClick={() => setSelectedJob(null)}
                  className="bg-gray-300 px-4 py-2 rounded"
                >
                  Cancel
                </button>
                <button
                  onClick={handleResolve}
                  disabled={remaining < 0}
                  className="bg-blue-700 text-white px-4 py-2 rounded disabled:opacity-50"
                >
                  Confirm Resolution
                </button>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default DisputeJobs;
